import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Ajout au panier depuis les cartes produit légères (snippets/max-product-card.liquid).
 *
 * Le markup est un vrai <form action="/cart/add" method="post"> : sans JavaScript, il poste
 * normalement et l'ajout fonctionne (rechargement + redirection panier). Ce module intercepte le
 * submit pour le faire en AJAX. Aucun custom element, là où le quick-add natif de Horizon en
 * démarrait trois par carte.
 *
 * Délégation sur `document` : un seul écouteur couvre toutes les cartes de la page, y compris
 * celles injectées après coup (onglets hydratés, re-render de filtres, contenu du cart-drawer).
 *
 * Module ES : évalué une seule fois par URL même si plusieurs balises le référencent.
 */

/** Durée d'affichage de « Ajouté » avant retour au libellé d'origine. */
const SUCCESS_MESSAGE_DURATION = 1500;

/**
 * Remet le libellé d'origine du bouton et réactive le formulaire.
 * @param {HTMLFormElement} form
 * @param {HTMLElement} button
 * @param {string} label
 */
function reset(form, button, label) {
  button.textContent = label;
  delete form.dataset.busy;
}

/**
 * @param {SubmitEvent} event
 */
async function handleSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form[data-max-quick-add]')) return;

  event.preventDefault();

  // Anti double-clic : la requête est en cours, on ignore.
  if (form.dataset.busy) return;
  form.dataset.busy = '1';

  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLElement)) return;

  const label = button.textContent?.trim() ?? '';
  const formData = new FormData(form);

  // Le drawer se rafraîchit tout seul via /cart/update.js (voir component-cart-items.js), donc
  // pas besoin de demander les sections ici : ce serait du rendu serveur payé pour rien.
  const config = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(`${form.action}.js`, config);
    const result = await response.json();

    // /cart/add.js renvoie un objet avec `status` quand l'ajout échoue (rupture, variante
    // invalide…). Le HTTP peut être 422 : on se fie à la charge utile, pas au seul response.ok.
    if (result.status) {
      form.dispatchEvent(new CartErrorEvent(form.id || 'max-quick-add', result.message, result.description, result.errors));
      button.textContent = result.message || label;
      setTimeout(() => reset(form, button, label), SUCCESS_MESSAGE_DURATION);
      return;
    }

    // Libellé fourni par Liquid : `Theme` est un `const` de script classique, donc absent de
    // `window` — on ne peut pas lire Theme.translations depuis un module.
    button.textContent = button.dataset.addedLabel || label;

    // Même contrat que product-form.js, dont dépendent trois composants :
    //   - cart-drawer.js      : ouvre le drawer si didError est absent/faux ;
    //   - cart-icon.js        : avec source 'product-form-component', itemCount est un DELTA
    //                           ajouté au compteur — sans cette source, il serait pris pour le
    //                           total absolu et la pastille afficherait 1 au lieu du vrai nombre ;
    //   - cart-free-gift.js   : resource vide ({}) le fait refetcher le panier réel.
    form.dispatchEvent(
      new CartAddEvent({}, form.id || 'max-quick-add', {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || 1,
        variantId: String(formData.get('id') ?? ''),
      })
    );

    setTimeout(() => reset(form, button, label), SUCCESS_MESSAGE_DURATION);
  } catch (error) {
    console.error('[max-quick-add]', error);
    reset(form, button, label);
  }
}

document.addEventListener('submit', handleSubmit);
